import React, { useEffect, useState } from "react";
import { FiFilter, FiSearch, FiStar, FiEye, FiChevronRight } from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { baseurl, imgurl } from "../baseurl";
import productDefault from "../assets/img/product.webp";
import Wishlist from "../components/Wishlist";

function Category() {
  const { category, subcategory } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        let url = `${baseurl}products/category/${category}`;
        if (subcategory) url += `/subcategory/${subcategory}`;
        const res = await axios.get(url);
        if (res.data.success) setProducts(res.data.products || []);
      } catch (err) {
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, subcategory]);

  const getBrand = (p) => {
    if (p.brand_name) return p.brand_name;
    const catAttr = p.product_attributes?.[p.category_name];
    return catAttr?.Brand || "Other";
  };

  const getDiscount = (price, mrp) => {
    const p = parseFloat(price);
    const m = parseFloat(mrp);
    if (!m || m <= p) return null;
    return Math.round(((m - p) / m) * 100);
  };

  const brands = Array.from(new Set(products.map((p) => getBrand(p))));
  
  const priceRanges = [
    { label: "Under ₹1,000", min: 0, max: 1000 },
    { label: "₹1,000 - ₹5,000", min: 1000, max: 5000 },
    { label: "Above ₹10,000", min: 10000, max: 999999 },
  ];

  const toggleFilter = (value, state, setState) => {
    if (state.includes(value)) setState(state.filter((v) => v !== value));
    else setState([...state, value]);
  };

  const filteredProducts = products.filter((p) => {
    const searchMatch = p.product_name.toLowerCase().includes(searchTerm.toLowerCase());
    const brandMatch = selectedBrands.length === 0 || selectedBrands.includes(getBrand(p));
    
    let priceMatch = true;
    if (selectedPriceRange.length) {
      const price = parseFloat(p.price);
      priceMatch = selectedPriceRange.some((label) => {
        const range = priceRanges.find((r) => r.label === label);
        return price >= range.min && price <= range.max;
      });
    }
    return searchMatch && brandMatch && priceMatch;
  });

  return (
    <div className="product-section my-4">
      <div className="container-fluid px-lg-5">
        
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div className="section-title">
            <span className="text-uppercase tracking-wider text-muted small fw-bold">Collection</span>
            <h3 className="fw-bold m-0 text-dark">{subcategory || category || "Products"}</h3>
            <div className="accent-line"></div>
          </div>
          <div className="position-relative d-none d-md-block" style={{width: '300px'}}>
              <input 
                  type="text" 
                  className="form-control ps-4 rounded-pill border-light shadow-sm" 
                  placeholder="Search in results..." 
                  onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FiSearch className="position-absolute top-50 translate-middle-y ms-2 text-muted" />
          </div>
        </div>

        <div className="row">
          {/* Sidebar Filters */}
          <aside className="col-md-3 col-xl-2 d-none d-md-block">
            <div className="filter-sidebar p-3 bg-white rounded-4 shadow-sm border">
              <div className="mb-4">
                <p className="fw-bold small text-uppercase mb-3" style={{letterSpacing: '1px'}}>Brand</p>
                {brands.map(b => (
                  <label key={b} className="d-flex align-items-center mb-2 small text-muted cursor-pointer hover-text-dark">
                    <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        onChange={() => toggleFilter(b, selectedBrands, setSelectedBrands)} 
                    />
                    {b}
                  </label>
                ))}
              </div>

              <div className="mb-4">
                <p className="fw-bold small text-uppercase mb-3" style={{letterSpacing: '1px'}}>Price</p>
                {priceRanges.map(r => (
                  <label key={r.label} className="d-flex align-items-center mb-2 small text-muted cursor-pointer hover-text-dark">
                     <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        onChange={() => toggleFilter(r.label, selectedPriceRange, setSelectedPriceRange)} 
                     />
                     {r.label}
                  </label>
                ))}
              </div>
            </div>
          </aside>

          {/* Product Grid */}
          <div className="col-md-9 col-xl-10">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status"></div>
                <p className="mt-2 text-muted">Curating your collection...</p>
              </div>
            ) : (
              <div className="row g-3">
                {filteredProducts.map((p) => {
                  const discount = getDiscount(p.price, p.mrp);
                  const savings = p.mrp - p.price;
                  
                  return (
                    <div key={p.id} className="col-6 col-md-4 col-lg-3">
                      <div className="card-inner shadow-sm h-100">
                        {discount > 0 && (
                          <div className="badge-discount">
                            <span className="discount-value">{discount}%</span>
                            <span className="discount-label">OFF</span>
                          </div>
                        )}
                        
                        <div className="card-actions">
                          <Wishlist productId={p.id} />
                           
                        </div>

                        <Link to={`/product/${p.slug}`} className="image-box">
                          <img
                            src={p.featured_image ? `${imgurl}${p.featured_image}` : productDefault}
                            alt={p.product_name}
                            loading="lazy"
                            onError={(e) => { e.target.src = productDefault; }}
                          />
                        </Link>

                        <div className="info-box">
                          <p className="sub-cat">{getBrand(p)}</p>
                          <Link to={`/product/${p.slug}`} className="product-name">
                            {p.product_name}
                          </Link>
                          
                          <div className="price-container">
                            <div className="d-flex align-items-center gap-2">
                              <span className="curr-price">₹{parseInt(p.price).toLocaleString("en-IN")}</span>
                              {discount > 0 && <span className="old-price">₹{parseInt(p.mrp).toLocaleString("en-IN")}</span>}
                            </div>
                            
                            {discount > 0 && (
                              <div className="savings-tag">
                                You Save ₹{savings.toLocaleString("en-IN")}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {!loading && filteredProducts.length === 0 && (
                <div className="text-center py-5">
                    <h5 className="text-muted">No products found matching your filters.</h5>
                </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .tracking-wider { letter-spacing: 1.5px; }
        .accent-line { width: 40px; height: 3px; background: #ff4757; margin-top: 8px; border-radius: 10px; }
        
        /* Reusing consistent card styles */
        .card-inner {
          background: #fff; border-radius: 20px; overflow: hidden; position: relative;
          border: 1px solid rgba(214, 214, 214, 0.83); 
          transition: 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .card-inner:hover { transform: translateY(-5px); box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important; }

        .badge-discount {
          position: absolute; top: 15px; left: 15px; z-index: 5;
          background: #ed712f; color: #fff; padding: 5px 12px;
          border-radius: 12px; display: flex; flex-direction: column; align-items: center;
          line-height: 1; box-shadow: 0 4px 10px rgba(237, 113, 47, 0.4);
        }
        .discount-value { font-size: 0.85rem; font-weight: 900; }
        .discount-label { font-size: 0.5rem; font-weight: 700; }

        .image-box { display: flex; align-items: center; justify-content: center; position: relative; height: 220px; overflow: hidden; background: #f9fafb; }
        .image-box img { width: 85%; height: 85%; object-fit: contain; transition: 0.8s ease; }
         

        .card-actions { position: absolute; top: 15px; right: -50px; display: flex; flex-direction: column; gap: 10px; transition: 0.3s ease; z-index: 10; }
        .card-inner:hover .card-actions { right: 15px; }
        
        .action-btn { 
            width: 38px; height: 38px; border-radius: 50%; background: #636363; border: none; 
            display: flex; align-items: center; justify-content: center; box-shadow: 0 5px 15px rgba(0,0,0,0.08);
            color: #f5f5f5; transition: 0.3s;
        }
        .action-btn:hover { background: #ed712f; color: #fff; transform: scale(1.1); }

        .info-box { padding: 15px; }
        .sub-cat { font-size: 0.65rem; font-weight: 600; color: #ed712f; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 5px; }
        .product-name { 
          font-size: .95rem; font-weight: 600; color: #1a202c; 
          text-decoration: none; display: -webkit-box; -webkit-line-clamp: 2; 
          -webkit-box-orient: vertical; overflow: hidden; margin-bottom: 8px;
          line-height: 1.4; height: 2.8em;
        }
        .curr-price { font-size: 1.2rem; font-weight: 900; color: #1a202c; }
        .old-price { font-size: 0.85rem; text-decoration: line-through; color: #a0aec0; font-weight: 500; }
        .savings-tag { 
          font-size: 0.7rem; color: #38a169; font-weight: 800; background: #f0fff4; 
          padding: 3px 8px; border-radius: 6px; width: fit-content; border: 1px solid #c6f6d5;
          margin-top: 5px;
        }

        .hover-text-dark:hover { color: #000 !important; }

        @media (max-width: 768px) {
          .image-box { height: 180px; }
          .card-actions { right: 10px; }
          .product-name { font-size: 0.85rem; }
        }
      `}</style>

      {/* Mobile Filter FAB (Optional - you can trigger your offcanvas here) */}
    </div>
  );
}

export default Category;